<script setup lang="ts">
import { computed, onMounted, unref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useField, useForm } from 'vee-validate';
import _ from 'lodash';

import FormActions from '@/components/FormActions.vue';
import DynamicTabs from '@/components/DynamicTabs.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import StandardInput from '@/components/StandardInput.vue';
import ImageField from '@/components/ImageField.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import CustomPageList from '@/components/CustomPageList.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import AdminPageList from '@/components/AdminPageList.vue';
import CommunityBrandingTab from '@/components/community/CommunityBrandingTab.vue';
import RoleVisibilityGuard from '@/components/RoleVisibilityGuard.vue';
import HomepageItemSelectionButton from '@/components/HomepageItemSelectionButton.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';

import { getLanguageEnabled, getTranslation, provideEditLocale } from '@/composables/data-display-utils';
import { useThemeOptions } from '@/composables/useThemeOptions';
import { useCommunityBrandingForm } from '@/composables/useCommunityBrandingForm';
import { buildBrandingFormValues } from '@/helpers/community-branding.helper';
import type { CommunityDto, Locales } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useCommunityStore, useConfigurationStore, useUsersStore } from '@/stores';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { formSaveImageGuard, formSaveNavigationGuard, resetFormToItem } from '@/helpers/form.helper';
import { useLanguageValidationErrors } from '@/composables';
import { Constants } from '@/constants';
import { communityValidationSchema } from '@/validation/communitySchema';

const { t } = useI18n();

// STORE
const communityStore = useCommunityStore();
const configurationStore = useConfigurationStore();
const userStore = useUsersStore();
const { currentUser } = storeToRefs(userStore);

const communityId = computed(() => communityStore.community?.id ?? -1);
const currentCommunity = computed(() => communityStore.communities.find((c) => c.id === communityId.value));
// todo: les traduction de YUP ne sont pas mise a jour sur changement de langue.
//  Il faudrait trouver un moyen de revalider le form sur changement de langue.
const languageEnabled = computed(() => getLanguageEnabled(currentCommunity.value));

// FORM
const validationSchema = computed(() => communityValidationSchema(languageEnabled.value));
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm, setFieldValue, values } = formContext;

const { value: languageEnabledFieldValue } = useField<Locales[]>('language_enabled');
const { value: defaultLanguage } = useField<Locales>('default_language');
watch(languageEnabledFieldValue, () => {
  if (!languageEnabledFieldValue.value.includes('fr')) {
    setFieldValue('options.loi96', false);
  }
  if (!languageEnabledFieldValue.value.includes(defaultLanguage.value)) {
    setFieldValue('default_language', null);
  }
});

const { value: enabledField } = useField<boolean>('options.google_tag_manager.enabled');
watch(enabledField, (newVal) => {
  if (!newVal) {
    setFieldValue('options.google_tag_manager.container_id', null);
  }
});
const brandingPresets = computed(() => configurationStore.getBrandingPresets());
useCommunityBrandingForm({ presets: brandingPresets, setFieldValue, values });

function withBrandingFormValues(community: CommunityDto): CommunityDto {
  return {
    ...community,
    options: {
      ...community.options,
      branding: buildBrandingFormValues(community.options?.branding, brandingPresets.value),
    },
  };
}

const community = useWatchItemLoading<CommunityDto>(communityId, communityStore, (f) =>
  resetFormToItem(formContext, withBrandingFormValues(f)),
);
const languageErrors = useLanguageValidationErrors(errors);

const editLocale = provideEditLocale(languageEnabledFieldValue);
const isSuperadmin = computed(() => Boolean(currentUser.value?.isSuperadmin));

const themeOptions = useThemeOptions('project', languageEnabledFieldValue);
const pageThemeOptions = useThemeOptions('page', languageEnabledFieldValue);

// COMPUTED
const isDirty = computed(() => {
  return unref(meta).dirty;
});

const tabs = computed(() => {
  const tabList = [
    { id: 'infos', name: t('community.tabs.infos'), isDirty: isDirty.value },
    { id: 'image', name: t('community.tabs.image') },
    { id: 'customPages', name: t('community.tabs.customPage') },
    { id: 'admins', name: t('community.tabs.admins') },
    { id: 'option', name: t('community.tabs.option') },
  ];

  if (isSuperadmin.value) {
    tabList.push({ id: 'branding', name: t('community.branding.title') });
  }

  return tabList;
});

const openedTab = computed(() => {
  const requestedTab = window.location.hash ? window.location.hash.substring(1) : 'infos';
  return tabs.value.some((tab) => tab.id === requestedTab) ? requestedTab : 'infos';
});

// LIFECYCLE
onMounted(async () => {
  await communityStore.fetchCommunityById(communityId.value);
});

onBeforeRouteLeave(formSaveNavigationGuard(meta));

const removeCommunityBanner = async () => {
  await communityStore.removeCommunityImage(communityId.value, 'banner');
};

const removeCommunityLogo = async () => {
  await communityStore.removeCommunityImage(communityId.value, 'logo');
};

const removeCommunityFavicon = async () => {
  await communityStore.removeCommunityImage(communityId.value, 'favicon');
};

const removeCommunityOpenGraph = async () => {
  await communityStore.removeCommunityImage(communityId.value, 'open-graph');
};

const removeCommunityHorizontalLogo = async () => {
  await communityStore.removeCommunityImage(communityId.value, 'horizontal-logo');
};

const handleClearBanner = async () => {
  formSaveImageGuard(isDirty.value, removeCommunityBanner);
};

const handleClearLogo = async () => {
  formSaveImageGuard(isDirty.value, removeCommunityLogo);
};
const handleClearFavicon = async () => {
  formSaveImageGuard(isDirty.value, removeCommunityFavicon);
};

const handleClearOpenGraph = async () => {
  formSaveImageGuard(isDirty.value, removeCommunityOpenGraph);
};

const handleClearHorizontalLogo = async () => {
  formSaveImageGuard(isDirty.value, removeCommunityHorizontalLogo);
};

const handleSaveBanner = async (options: SavingOptions) => {
  await communityStore.saveCommunityImage(communityId.value, 'banner', options);
};

const handleSaveLogo = async (options: SavingOptions) => {
  await communityStore.saveCommunityImage(communityId.value, 'logo', options);
};

const handleSaveFavicon = async (options: SavingOptions) => {
  await communityStore.saveCommunityImage(communityId.value, 'favicon', options);
};

const handleSaveOpenGraph = async (options: SavingOptions) => {
  await communityStore.saveCommunityImage(communityId.value, 'open-graph', options);
};

const handleSaveHorizontalLogo = async (options: SavingOptions) => {
  await communityStore.saveCommunityImage(communityId.value, 'horizontal-logo', options);
};

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await communityStore.saveCommunity(values as CommunityDto),
    () => resetForm({ values: withBrandingFormValues(community.value!) }),
  );
});

const onCancel = () => {
  if (community.value) {
    resetForm({ values: withBrandingFormValues(community.value) });
  } else {
    console.warn('onCancel: form is null');
  }
};

// FUNCTIONS
function onFeaturedProjectSelection(row: any) {
  setFieldValue('options.featured_project_id', row.id);
}

function clearFeaturedProjectId() {
  setFieldValue('options.featured_project_id', null);
}

function getLanguages(): { label: string; value: string }[] {
  return languageEnabledFieldValue.value.map((locale) => {
    return { label: _.capitalize(t(`languages.${locale}`)), value: locale };
  });
}
</script>

<template>
  <q-page class="community-detail">
    <!-- <div class="navigation">
            <back-button to="/communities"/>
        </div> -->
    <div class="title">
      <h1>{{ getTranslation(community, 'title', languageEnabledFieldValue) }}</h1>
    </div>
    <div v-if="community">
      <dynamic-tabs :tabs="tabs" :active="openedTab" keep-alive>
        <template #infos>
          <div class="column">
            <form-actions
              v-model:editLocale="editLocale"
              :onCancel="onCancel"
              :onSubmit="onSubmit"
              :languageErrors="languageErrors"
              :meta="meta"
              :languageEnabled="languageEnabledFieldValue"
            />
            <standard-input name="slug" :label="t('community.slug')" outlined :disabled="!currentUser?.isSuperadmin" />
            <standard-toggle name="custom_url.enabled" :label="t('community.custom_url.enabled')" outlined />
            <standard-input
              name="custom_url.hostname"
              :label="t('community.custom_url.hostname')"
              outlined
              :disabled="!currentUser?.isSuperadmin"
              prefix="https://"
            />
            <localized-input
              name="title"
              :label="t('community.title')"
              :editLocale="editLocale"
              :language_enabled="languageEnabledFieldValue"
              outlined
            />
            <localized-input
              name="description"
              :label="t('community.description')"
              :editLocale="editLocale"
              type="textarea"
              :language_enabled="languageEnabledFieldValue"
              outlined
            />
            <localized-input
              name="organization_name"
              :label="t('community.organizationName')"
              :editLocale="editLocale"
              :language_enabled="languageEnabledFieldValue"
              outlined
            />
            <localized-input
              name="footer_description"
              :label="t('community.footer-description')"
              :editLocale="editLocale"
              type="textarea"
              :language_enabled="languageEnabledFieldValue"
              outlined
            />
            <q-separator :spaced="true" />
            <p class="text-h6">
              {{ t('community.contactInfo') }} &nbsp;<span class="text-subtitle2">{{
                t('community.contactInfoVisibility')
              }}</span>
            </p>

            <standard-input name="contact_info.phone" :label="t('community.phone')" outlined />
            <standard-input name="contact_info.email" :label="t('community.email')" outlined />
            <localized-input
              name="contact_info.address"
              type="textarea"
              :label="t('community.address')"
              :editLocale="editLocale"
              :language_enabled="languageEnabledFieldValue"
              outlined
            />
            <localized-input
              name="contact_info.website"
              :label="t('community.website')"
              :editLocale="editLocale"
              :language_enabled="languageEnabledFieldValue"
              outlined
            />
            <q-separator :spaced="true" />
          </div>
        </template>
        <template #image>
          <form-actions
            v-model:editLocale="editLocale"
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            :languageEnabled="languageEnabledFieldValue"
          />
          <div class="column">
            <image-field
              :label="t('community.image.banner.label')"
              :source="community.banner_file?.url"
              :target-width="1920"
              :target-height="360"
              @clear-image="handleClearBanner"
              @save-image="handleSaveBanner"
              :form-dirty="isDirty"
              :language-enabled="languageEnabledFieldValue"
              alt-text-name="banner_file.altText"
            />
            <image-field
              :label="t('community.image.logo.label')"
              :source="community.logo_file?.url"
              :target-width="120"
              :target-height="120"
              @clear-image="handleClearLogo"
              @save-image="handleSaveLogo"
              :form-dirty="isDirty"
              :colorPicker="true"
              backgroundColor="logo_file.color"
              :language-enabled="languageEnabledFieldValue"
              alt-text-name="logo_file.altText"
            />
            <image-field
              :label="t('community.image.favicon.label')"
              :source="community.favicon_file?.url"
              :target-width="64"
              :target-height="64"
              @clear-image="handleClearFavicon"
              @save-image="handleSaveFavicon"
              :form-dirty="isDirty"
              :language-enabled="languageEnabledFieldValue"
              alt-text-name="favicon_file.altText"
            />
            <image-field
              :label="t('global.label.social-media-image')"
              :source="community.open_graph_file?.url"
              :target-width="1200"
              :target-height="630"
              @clear-image="handleClearOpenGraph"
              @save-image="handleSaveOpenGraph"
              :form-dirty="isDirty"
              :language-enabled="languageEnabledFieldValue"
              alt-text-name="open_graph_file.altText"
            />
            <image-field
              :label="t('global.label.horizontal-logo')"
              :source="community.horizontal_logo_file?.url"
              :target-width="1000"
              :target-height="200"
              @clear-image="handleClearHorizontalLogo"
              @save-image="handleSaveHorizontalLogo"
              :form-dirty="isDirty"
              :language-enabled="languageEnabledFieldValue"
              type="horizontal"
              alt-text-name="horizontal_logo_file.altText"
            />
          </div>
        </template>
        <template #option>
          <form-actions
            :show-language-switcher="false"
            v-model:editLocale="editLocale"
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            :languageEnabled="languageEnabledFieldValue"
          />
          <q-page>
            <role-visibility-guard :requires-superadmin="true">
              <q-card class="q-mb-md">
                <q-card-section>
                  <q-item-label header class="text-h6">Général</q-item-label>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <standard-toggle
                    name="options.use_demographics"
                    :label="t('community.options.use-demographics')"
                    outlined
                  />
                  <localized-select
                    name="default_page_theme_key"
                    :label="t('community.default-page-theme')"
                    :options="pageThemeOptions"
                    emit-value
                    map-options
                    outlined
                    class="q-mt-md"
                  />
                  <localized-select
                    name="default_project_theme_key"
                    :label="t('community.default-project-theme')"
                    :options="themeOptions"
                    emit-value
                    map-options
                    outlined
                    class="q-mt-md"
                  />
                </q-card-section>
              </q-card>
            </role-visibility-guard>
            <role-visibility-guard :requires-superadmin="true">
              <q-card class="q-mb-md">
                <q-card-section>
                  <q-item-label header class="text-h6">{{ t('community.languages') }}</q-item-label>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <language-selector name="language_enabled" :label="t('community.language_enabled')" />
                  <localized-select
                    name="default_language"
                    :label="$t('community.default-language')"
                    :editLocale="editLocale"
                    :options="getLanguages()"
                    emit-value
                    map-options
                    outlined
                  />
                  <q-separator :spaced="true" />
                  <standard-toggle
                    name="options.loi96"
                    :label="t('community.loi96')"
                    :disabled="!languageEnabledFieldValue?.includes('fr')"
                    outlined
                  />
                </q-card-section>
              </q-card>
            </role-visibility-guard>
            <role-visibility-guard :requires-superadmin="true">
              <q-card class="q-mb-md">
                <q-card-section>
                  <q-item-label header class="text-h6">
                    {{ t('community.seoAndAnalytics') }}
                  </q-item-label>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <standard-toggle
                    name="options.allow_indexing"
                    :label="t('community.options.allowIndexing')"
                    outlined
                  />
                  <standard-toggle
                    name="options.google_tag_manager.enabled"
                    :label="t('community.options.google_tag_manager.enabled')"
                    v-model="enabledField"
                    outlined
                  />
                  <standard-input
                    name="options.google_tag_manager.container_id"
                    :label="t('community.options.google_tag_manager.container_id')"
                    :disabled="!enabledField"
                    outlined
                  />
                </q-card-section>
              </q-card>
            </role-visibility-guard>
            <role-visibility-guard :requires-superadmin="true">
              <q-card class="q-mb-md">
                <q-card-section>
                  <q-item-label header class="text-h6">Export</q-item-label>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <standard-toggle name="options.export.pdf" :label="t('community.options.export.pdf')" outlined />
                  <standard-toggle
                    name="options.export.ai_analysis"
                    :label="t('community.options.export.ai_analysis')"
                    outlined
                  />
                </q-card-section>
              </q-card>
            </role-visibility-guard>
            <role-visibility-guard :requires-superadmin="true">
              <q-card class="q-mb-md">
                <q-card-section>
                  <q-item-label header class="text-h6">Consultations</q-item-label>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <standard-toggle
                    name="options.consultation.map"
                    :label="t('community.options.consultation.map')"
                    outlined
                    disabled
                  />
                  <standard-toggle
                    name="options.consultation.moderation_comment"
                    :label="t('community.options.consultation.moderation_comment')"
                    outlined
                    disabled
                  />
                  <standard-toggle
                    name="options.consultation.citizen_proposal"
                    :label="t('community.options.consultation.citizen_proposal')"
                    outlined
                  />
                  <standard-toggle
                    name="options.consultation.guided_consultation"
                    :label="t('community.options.consultation.guided_consultation')"
                    outlined
                  />
                </q-card-section>
              </q-card>
            </role-visibility-guard>
            <q-card class="q-mb-md">
              <q-card-section>
                <q-item-label header class="text-h6">
                  {{ t('community.options.featuredProject.title') }}
                </q-item-label>
              </q-card-section>
              <q-separator />
              <q-card-section>
                {{ t('community.options.featuredProject.text') }}
                <HomepageItemSelectionButton
                  value="options.featured_project_id"
                  @selection="onFeaturedProjectSelection"
                  @clear="clearFeaturedProjectId"
                  :item-type="Constants.Dashboard.Type.Project"
                  :language-enabled="languageEnabledFieldValue"
                />
              </q-card-section>
            </q-card>
            <q-card class="q-mb-md">
              <q-card-section>
                <q-item-label header class="text-h6">{{ t('community.options.social-networks.title') }}</q-item-label>
              </q-card-section>
              <q-separator />
              <q-card-section>
                {{ t('community.options.social-networks.text') }}
                <standard-toggle name="options.social_networks.facebook.enabled" label="Facebook" outlined />
                <standard-input
                  name="options.social_networks.facebook.url"
                  :label="t('community.slug')"
                  :disabled="!values.options?.social_networks?.facebook?.enabled"
                  outlined
                />
                <standard-toggle name="options.social_networks.instagram.enabled" label="Instagram" outlined />
                <standard-input
                  name="options.social_networks.instagram.url"
                  :label="t('community.slug')"
                  :disabled="!values.options?.social_networks?.instagram?.enabled"
                  outlined
                />
                <standard-toggle name="options.social_networks.linkedin.enabled" label="LinkedIn" outlined />
                <standard-input
                  name="options.social_networks.linkedin.url"
                  :label="t('community.slug')"
                  :disabled="!values.options?.social_networks?.linkedin?.enabled"
                  outlined
                />
                <standard-toggle name="options.social_networks.x.enabled" label="X" outlined />
                <standard-input
                  name="options.social_networks.x.url"
                  :label="t('community.slug')"
                  :disabled="!values.options?.social_networks?.x?.enabled"
                  outlined
                />
              </q-card-section>
            </q-card>
          </q-page>
        </template>
        <template v-if="isSuperadmin" #branding>
          <form-actions
            :show-language-switcher="false"
            v-model:editLocale="editLocale"
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            :languageEnabled="languageEnabledFieldValue"
          />
          <community-branding-tab />
        </template>
        <template #customPages>
          <custom-page-list :languageEnabled="languageEnabledFieldValue" />
        </template>
        <template #admins>
          <admin-page-list :community_id="communityId" />
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
<style lang="scss">
.community-detail {
  padding: 16px;

  .navigation {
    display: flex;
    flex-direction: row;
    padding: 0 8px;
  }

  .title {
    margin: 20px 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 10px;
    gap: 10px;
  }
}
</style>
